import { Stack, Box, FormControlLabel, Switch, Typography } from '@mui/material'
import { themeNew } from 'styles/theme-new'
import CustomDateInput from './custom-date-input'
import DatedRightGroup from './dated-right-group'

type GlobalDataProps = {
  enabled: boolean
  startDateValue: string | null
  endDateValue: string | null
  label: string
  startDateLabel: string
  endDateLabel: string
  isLoading: boolean
  onToggle: () => void
  onStartDateChange: (isoDate: string | null) => void
  onEndDateChange: (isoDate: string | null) => void
}

// ENG2-2395 — the Global Data toggle plus the two contract dates.
//
// The dates are not symmetric, and the difference is the thing CS most needs to
// see: the start date is a record of when the contract began and never affects
// access, while the end date is what actually closes global data. An empty end
// date means the contract auto-renews, which is the normal case — hence the
// helper text rather than leaving operators to infer it from a blank field.
function GlobalData({
  enabled,
  startDateValue,
  endDateValue,
  label,
  startDateLabel,
  endDateLabel,
  isLoading,
  onToggle,
  onStartDateChange,
  onEndDateChange,
}: GlobalDataProps) {
  const handleToggle = () => {
    if (isLoading) return
    onToggle()
  }

  return (
    // ENG2-2633: toggle + dates read as one unit while the dates are shown.
    <DatedRightGroup grouped={enabled}>
      <FormControlLabel
        sx={{
          m: 0,
          '& .MuiFormControlLabel-label': {
            fontSize: '0.875rem',
          },
        }}
        control={
          <Switch
            size="small"
            disabled={isLoading}
            color="default"
            style={{
              color: enabled
                ? themeNew.palette.success[500]
                : themeNew.palette.grey[600],
            }}
            checked={enabled}
            onChange={handleToggle}
          />
        }
        label={
          <Typography
            variant="body2"
            color={enabled ? 'text.primary' : 'text.secondary'}
          >
            {label}
          </Typography>
        }
      />
      {enabled && (
        // Stacked, end date under start date — they read as a period, and the
        // end date's helper text sits directly under the field it explains.
        <Stack direction="column" spacing={2} sx={{ mt: 1 }}>
          {/* minWidth/flex mirror `fieldContainer` on the customer-level
              Expiration Date field (ENG2-2398). */}
          <Box sx={{ minWidth: 160, flex: '0 0 auto' }}>
            <CustomDateInput
              value={startDateValue}
              label={startDateLabel}
              isLoading={isLoading}
              onChange={onStartDateChange}
            />
          </Box>
          <Box sx={{ minWidth: 160, flex: '0 0 auto' }}>
            <CustomDateInput
              value={endDateValue}
              label={endDateLabel}
              isLoading={isLoading}
              onChange={onEndDateChange}
              // Mirrors the backend's validate_period: an end date may not sit
              // before the start, nor in the past. Blocking it here means CS
              // sees the problem in the field instead of a failed PUT.
              minDate={startDateValue}
              disablePast
              helperText="Leave empty to auto-renew"
            />
          </Box>
        </Stack>
      )}
    </DatedRightGroup>
  )
}

export default GlobalData
